/// <amd-module name="Artifacts/Feed/redux/Sagas" />

import { getPermissionDelta } from "Artifacts/Feed/helpers/ArtifactsSettingsHelpers";
import {
  ArtifactsSettingsActions,
  ArtifactsSettingsActionTypes,
  CreateFeedActions,
  CreateFeedActionTypes,
  FeedHubActions,
  FeedHubActionTypes,
  IArtifactsSettingsPayload,
  ICreateFeedPayload,
  ILoadArtifactsSettingsPayload,
  IPackageListLoadPayload,
  IProblemPackageListLoadPayload,
} from "Artifacts/Feed/redux/Actions";
import * as Res from "Artifacts/Feed/Resources";
import {
  ArtifactsSettingsService,
  IArtifactsSettingsData,
} from "Artifacts/Feed/services/ArtifactsSettingsService";
import { CreateFeedService } from "Artifacts/Feed/services/CreateFeedService";
import { IPackageListService } from "Artifacts/Feed/services/PackageListService";
import { IProblemPackageListService } from "Artifacts/Feed/services/ProblemPackageListService";
import { ProblemPackageItem } from "Artifacts/Feed/types/ProblemPackageItem";
import { RootActions } from "Artifacts/Root/redux/Actions";
import { call, getContext, put, takeEvery } from "Artifacts/Root/Saga";
import { IFeedIdentity } from "Artifacts/Root/types";
import {
  CreateFeedAdditionalData,
  FeedMessage,
  MessageType,
  PackageListDataItem,
} from "Artifacts/Root/types/AzureArtifacts";
import { Feed } from "Artifacts/Root/web-apis/contracts/Feed";
import { SagaIterator } from "redux-saga";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

export default function* rootSaga(): SagaIterator {
  yield takeEvery(FeedHubActionTypes.LoadPackageList, initializePackages);
  yield takeEvery(CreateFeedActionTypes.LoadCreateFeedParams, loadCreateFeedParams);
  yield takeEvery(CreateFeedActionTypes.CreateFeed, createFeed);
  yield takeEvery(
    ArtifactsSettingsActionTypes.LoadArtifactsSettingsParams,
    loadArtifactsSettingsParams,
  );
  yield takeEvery(ArtifactsSettingsActionTypes.SaveArtifactsSettings, saveArtifactsSettings);
  yield takeEvery(FeedHubActionTypes.LoadProblemPackageList, initializeProblemPackages);
}

function* initializePackages(
  action: ActionWithPayload<FeedHubActionTypes.LoadPackageList, IPackageListLoadPayload>,
): SagaIterator {
  try {
    const { feed, view, params, fetchType } = action.payload;

    if (fetchType === "initial") {
      announce(Utils_String.format(Res.PackagesLoading_Announcement, feed.name));
      yield put(FeedHubActions.loadPackageListStarted());
    }

    if (feed?.isDeleted) {
      yield put(FeedHubActions.loadPackageListCompleted([]));
    } else {
      const context: IVssPageContext = (yield getContext("pageContext")) as any;
      const packageListService: IPackageListService = context.getService("IPackageListService");

      const packages = ((yield call(
        [packageListService, packageListService.getPackageList],
        context,
        feed,
        view,
        params,
      )) as unknown) as PackageListDataItem[];

      if (fetchType === "initial" || fetchType === "filtering") {
        if (packages.length > 0) {
          announce(Utils_String.format(Res.PackagesLoaded_Announcement, feed.name));
        } else {
          announce(Utils_String.format(Res.PackagesLoadedNoPackages_Announcement, feed.name));
        }
        yield put(FeedHubActions.loadPackageListCompleted(packages));
      } else if (fetchType === "amend") {
        yield put(FeedHubActions.packageListAmendPackages(packages));
      }
    }
  } catch (error) {
    announce(Utils_String.format(Res.PackagesLoadError_Announcement, action.payload.feed.name));
    yield put(FeedHubActions.loadPackageListFailed(error.message));
  }
}

function* initializeProblemPackages(
  action: ActionWithPayload<
    FeedHubActionTypes.LoadProblemPackageList,
    IProblemPackageListLoadPayload
  >,
): SagaIterator {
  try {
    const { feed } = action.payload;

    announce(Utils_String.format(Res.PackagesLoading_Announcement, feed.name));
    yield put(FeedHubActions.loadProblemPackageListStarted());

    const context: IVssPageContext = (yield getContext("pageContext")) as any;
    const problemPackageListService: IProblemPackageListService = context.getService(
      "IProblemPackageListService",
    );

    const packages = ((yield call(
      [problemPackageListService, problemPackageListService.getPackageList],
      context,
      feed,
    )) as unknown) as ProblemPackageItem[];

    // Fix the announcements for both cases.
    if (packages && packages.length > 0) {
      // Change to announce that there are problem packages.
      announce(Utils_String.format(Res.ProblemPackagesLoaded_Announcement, feed.name));

      const message = {
        id: feed.id + "_UnavailablePackages",
        message: Utils_String.format(Res.ProblemPackagesMessageBar_Message, packages.length),
        messageType: MessageType.Warning,
      } as FeedMessage;
      yield put(RootActions.showFeedMessages([message]));
    } else {
      announce(Utils_String.format(Res.ProblemPackagesLoadedNoPackages_Announcement, feed.name));
    }

    yield put(FeedHubActions.loadProblemPackageListCompleted(packages));
  } catch (error) {
    announce(
      Utils_String.format(Res.ProblemPackagesLoadError_Announcement, action.payload.feed.name),
    );
    yield put(FeedHubActions.loadProblemPackageListFailed(error.message));
  }
}

function* loadCreateFeedParams(): SagaIterator {
  try {
    yield put(CreateFeedActions.loadCreateFeedparamsStarted());
    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const createFeedService: CreateFeedService = pageContext.getService("ICreateFeedService");

    const createFeedServiceParams: CreateFeedAdditionalData = ((yield call([
      createFeedService,
      createFeedService.getCreateFeedServiceParams,
    ])) as unknown) as CreateFeedAdditionalData;

    yield put(CreateFeedActions.loadCreateFeedParamsCompleted(createFeedServiceParams));
  } catch (error) {
    yield put(CreateFeedActions.loadCreateFeedParamsFailed(error.message));
  }
}

function* createFeed(
  action: ActionWithPayload<CreateFeedActionTypes.CreateFeed, ICreateFeedPayload>,
): SagaIterator {
  try {
    const { createFeedData, feedServiceParams, handleCreatedFeed } = action.payload;

    yield put(CreateFeedActions.createFeedStarted());
    announce(Res.CreateFeed_AnnounceStart);
    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const createFeedService: CreateFeedService = pageContext.getService("ICreateFeedService");

    const createdFeed: Feed = ((yield call(
      [createFeedService, createFeedService.createFeed],
      createFeedData,
      feedServiceParams,
    )) as unknown) as Feed;

    announce(Utils_String.format(Res.CreateFeed_AnnounceSuccess, createdFeed.name), true);
    yield put(
      CreateFeedActions.createFeedCompleted({ ...createdFeed, isDeleted: false } as IFeedIdentity),
    );
    yield call(handleCreatedFeed, createdFeed.name);
  } catch (error) {
    announce(Utils_String.format(Res.CreateFeed_AnnounceError, error.message));
    yield put(CreateFeedActions.createFeedFailed(error.message));
  }
}

function* loadArtifactsSettingsParams(
  action: ActionWithPayload<
    ArtifactsSettingsActionTypes.LoadArtifactsSettingsParams,
    ILoadArtifactsSettingsPayload
  >,
): SagaIterator {
  try {
    yield put(ArtifactsSettingsActions.loadArtifactsSettingsStarted());
    const { feed } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const artifactsSettingsService: ArtifactsSettingsService = pageContext.getService(
      "IArtifactsSettingsService",
    );
    const artifactsSettingsData: IArtifactsSettingsData | undefined = yield call(
      [artifactsSettingsService, artifactsSettingsService.getArtifactsSettingsParams],
      feed,
    );

    yield put(ArtifactsSettingsActions.loadArtifactsSettingsCompleted(artifactsSettingsData));
  } catch (error) {
    yield put(ArtifactsSettingsActions.loadArtifactsSettingsFailed(error.message));
  }
}

function* saveArtifactsSettings(
  action: ActionWithPayload<
    ArtifactsSettingsActionTypes.SaveArtifactsSettings,
    IArtifactsSettingsPayload
  >,
): SagaIterator {
  try {
    yield put(ArtifactsSettingsActions.saveArtifactsSettingsStarted());
    const { artifactsSettingsData, feed, handleSaveSuccessful } = action.payload;
    const everyoneGroupId = feed.accountMetadata.everyoneGroupId;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const artifactsSettingsService: ArtifactsSettingsService = pageContext.getService(
      "IArtifactsSettingsService",
    );

    // since we're submitting the permission data diff, first reload the permission data
    const currentArtifactsSettingsData: IArtifactsSettingsData | undefined = yield call(
      [artifactsSettingsService, artifactsSettingsService.getArtifactsSettingsParams],
      feed,
    );

    const defaultData = {
      adminIds: [],
      creatorIds: [],
      customNames: [],
      everyoneIsCreator: true,
    } as IArtifactsSettingsData;

    const modifiedRoles = getPermissionDelta(
      currentArtifactsSettingsData || defaultData,
      artifactsSettingsData,
      everyoneGroupId,
    );

    announce(Res.ArtifactsSettings_AnnounceStart);
    yield call(
      [artifactsSettingsService, artifactsSettingsService.saveArtifactsSettings],
      modifiedRoles,
    );
    announce(Res.ArtifactsSettings_AnnounceSuccess);
    yield put(ArtifactsSettingsActions.saveArtifactsSettingsCompleted());
    yield call(handleSaveSuccessful);
  } catch (error) {
    announce(Utils_String.format(Res.ArtifactsSettings_AnnounceError, error.message));
    yield put(ArtifactsSettingsActions.saveArtifactsSettingsFailed(error.message));
  }
}
